import { useNavigate, useParams } from 'react-router-dom'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Form, ScoreElement, SectionElement, useCreateForm } from 'modules/forms'

import { BasicInfoStep } from './BasicInfoStep'
import { FormTypeStep } from './FormTypeStep'
import { FormWizardData } from './types'

export const NewFormWizard = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams<'id'>()
  const { company, access } = useCurrentSession()
  const { createForm, creatingForm } = useCreateForm()
  const isTemplate = !id ? true : false // Check if we are using resourceId, to create a template or survey
  const { has_dev_forms_ai_cocreation, loadingFeatures } = useFeatures(['dev_forms_ai_cocreation'])
  const initialData: FormWizardData = {
    anonymous: false,
    title: '',
    description: '',
    trainingFormType: 'satisfaction',
  }

  const form = useForm<FormWizardData>({
    defaultValues: initialData,
    onSubmit: async (data) => {
      if (!id && !isTemplate) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.surveys.create.error.invalid_id'),
        })
        return {
          success: false,
          errors: [
            { type: 'generic_error', message: i18n.t('trainings.surveys.create.error.invalid_id') },
          ],
        }
      }

      const form = Form.create({
        title: data.title,
        resourceId: id ?? '',
        resourceType: 'Trainings::Training',
        anonymous: data.trainingFormType === 'satisfaction' ? data.anonymous : false, //only satisfaction surveys can be anonymous
        description: data.description,
        formType: data.trainingFormType,
      })

      if (data.trainingFormType === 'effectiveness' || data.trainingFormType === 'satisfaction') {
        // satisfaction anb effectiveness surveys have a mandatory score, so we add
        // the section and the score to the form
        const section = SectionElement.create({
          label:
            data.trainingFormType === 'effectiveness'
              ? i18n.t('trainings.surveys.create.effectiveness.overall_effectiveness.title')
              : i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.title'),
          locked: true,
        })

        const score = ScoreElement.create({
          label:
            data.trainingFormType === 'effectiveness'
              ? i18n.t('trainings.surveys.create.effectiveness.overall_effectiveness.content')
              : i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.content'),
          scoreType:
            data.trainingFormType === 'effectiveness'
              ? 'courseeffectiveness'
              : 'coursesatisfaction',
        })

        section.addElement(score)
        form.addElement(section)
      }

      const handled = await createForm(form, company.id, access.id)
      if (handled.success && handled.data.form) {
        app.setFlash({
          type: 'correct',
          message: isTemplate
            ? i18n.t('forms.custom_templates.data_collection.create_template.success')
            : i18n.t('trainings.surveys.create.success'),
        })
        if (isTemplate) {
          navigate(
            navigation.trainings.list.surveyTemplates.templateDetails.edit.fullPath({
              templateId: handled.data.form.id,
            })
          )
        } else if (id) {
          if (has_dev_forms_ai_cocreation) {
            navigate(
              navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.fullPath({
                id: id,
                formId: handled.data.form.id,
              })
            )
          } else {
            navigate(
              navigation.trainings.list.details_revamp.trainingSurveyDetail.edit.fullPath({
                id: id,
                formId: handled.data.form.id,
              })
            )
          }
        }
      }
      return handled
    },
  })

  const formHandleSubmit = form.handleSubmit
  const onSubmit = useUnsafeCallback(() => formHandleSubmit(), [formHandleSubmit])

  const surveyTypeStep: WizardStep<FormWizardData> = {
    title: i18n.t('trainings.surveys.create.survey_type'),
    nextText: i18n.t('common.next'),
    render: (props) => <FormTypeStep {...props} />,
    isCompleted: () => !loadingFeatures, // Ensure that the feature flag is loaded
  }
  const basicInfoStep: WizardStep<FormWizardData> = {
    title: i18n.t('trainings.surveys.create.basic_information'),
    nextText: i18n.t('common.save'),
    render: (props) => <BasicInfoStep {...props} />,
    isCompleted: () => Boolean(form.values.title) && !creatingForm,
  }
  return (
    <Wizard.Modal
      icon="server-edit"
      title={
        !isTemplate
          ? i18n.t('trainings.surveys.create.new_survey')
          : i18n.t('forms.custom_templates.new_survey_template')
      }
      steps={[surveyTypeStep, basicInfoStep]}
      initialData={initialData}
      onSubmit={onSubmit}
      onDataChanged={form.setValues}
      onClose={() => {
        if (isTemplate) {
          navigate(navigation.trainings.list.surveyTemplates.list.fullPath())
        } else if (id) {
          navigate(
            navigation.trainings.list.details_revamp.view.surveys.fullPath({ id: String(id) })
          )
        }
      }}
      size="large"
    />
  )
}
